import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useCreateFeedback } from '../hooks/useFeedbackApi'
import { useT } from '../i18n/context'
import { errText } from '../lib/errors'
import { feedbackCategoryMeta } from '../lib/labelMaps'
import { useAuthStore } from '../stores/auth'
import { toast } from '../stores/toast'
import type { FeedbackCategory } from '../types/api'
import { Button } from './ui/Button'
import { Dialog } from './ui/Dialog'
import { Field, Select, Textarea } from './ui/Field'

const MESSAGE_MAX = 2000

/**
 * Feedback-Modal (Einstieg: Zahnrad-Menü). Der Nutzer wählt Kategorie und
 * schreibt Freitext; Mailadresse und aktueller Pfad werden automatisch
 * mitgesendet — die Mail übernimmt der Server aus dem JWT, der Pfad aus
 * der aktuellen Route (Muster usePageviews), beides nicht editierbar.
 */
export function FeedbackDialog({
    open,
    onClose,
}: {
    open: boolean
    onClose: () => void
}) {
    const t = useT()
    const location = useLocation()
    const create = useCreateFeedback()
    const email = useAuthStore(s => s.user?.email ?? '')
    const [category, setCategory] = useState<FeedbackCategory>('BUG')
    const [message, setMessage] = useState('')
    const [initializedFor, setInitializedFor] = useState<string | null>(null)

    // State-Reset während des Renders statt Effect (initializedFor-Muster):
    // jeder Öffnen-Vorgang startet mit leeren Feldern.
    const openKey = open ? 'feedback' : null
    if (openKey !== initializedFor) {
        setInitializedFor(openKey)
        if (openKey !== null) {
            setCategory('BUG')
            setMessage('')
        }
    }

    const currentPath = location.pathname + location.search

    const submit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!message.trim()) return
        create.mutate(
            { category, message: message.trim(), path: currentPath },
            {
                onSuccess: () => {
                    toast.success(t('feedback.sent'))
                    onClose()
                },
                onError: err => toast.error(errText(err)),
            },
        )
    }

    return (
        <Dialog open={open} onClose={onClose} title={t('feedback.title')}>
            <form onSubmit={submit} className='space-y-4'>
                <p className='text-sm text-slate-500 dark:text-slate-400'>
                    {t('feedback.intro')}
                </p>
                <Field label={t('feedback.category')}>
                    <Select
                        value={category}
                        onChange={e => setCategory(e.target.value as FeedbackCategory)}
                    >
                        {(Object.keys(feedbackCategoryMeta) as FeedbackCategory[]).map(
                            key => (
                                <option key={key} value={key}>
                                    {t(feedbackCategoryMeta[key].labelKey)}
                                </option>
                            ),
                        )}
                    </Select>
                </Field>
                <Field label={t('feedback.message')}>
                    <Textarea
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        placeholder={t('feedback.messagePlaceholder')}
                        required
                        maxLength={MESSAGE_MAX}
                        rows={5}
                        className='min-h-28'
                    />
                </Field>
                <div className='space-y-1 rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2.5 text-xs text-slate-500 dark:text-slate-400'>
                    <p>
                        <span className='font-medium text-slate-600 dark:text-slate-300'>
                            {t('feedback.currentPage')}:
                        </span>{' '}
                        <span className='font-mono'>{currentPath}</span>
                    </p>
                    <p>
                        <span className='font-medium text-slate-600 dark:text-slate-300'>
                            {t('feedback.sentAs')}:
                        </span>{' '}
                        {email}
                    </p>
                </div>
                <div className='flex justify-end gap-2'>
                    <Button
                        type='button'
                        variant='secondary'
                        onClick={onClose}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type='submit'
                        loading={create.isPending}
                        disabled={!message.trim()}
                    >
                        {t('feedback.send')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}
